import { useState, useLayoutEffect, useCallback, useMemo, type ReactNode } from "react";
import { ThemeContext, type Theme } from "./useTheme";

const STORAGE_KEY = "bastion-theme";

/**
 * Read the stored theme. Only "dark" selects the dark theme. Any other value,
 * including the old "system" value, reads as light.
 */
function getStoredTheme(): Theme {
  try {
    return localStorage.getItem(STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    // localStorage may be unavailable
    return "light";
  }
}

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  root.classList.toggle("dark", theme === "dark");
  root.setAttribute("data-surface", "app");
  // The browser bar takes the colour of the app chrome (`--bg`). A meta tag
  // cannot read a custom property, so copy the computed value.
  const meta = document.querySelector('meta[name="theme-color"]');
  const chrome = getComputedStyle(root).getPropertyValue("--bg").trim();
  if (meta && chrome) meta.setAttribute("content", chrome);
}

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
}

export function ThemeProvider({ children, defaultTheme }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(
    () => defaultTheme ?? getStoredTheme(),
  );

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // localStorage may be unavailable
    }
  }, []);

  // A layout effect, so the class is on <html> before the effects of the
  // children run. Canvas charts read token values in their effects.
  useLayoutEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const value = useMemo(
    () => ({ theme, setTheme, resolvedTheme: theme }),
    [theme, setTheme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}
